import { useMemo } from "react";
import { AlertTriangle, User } from "lucide-react";

import { Button } from "@/components/_atoms/button";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { toTrimmedQuestionLabels } from "@/domain/data-request/lib/free-text-questions";
import { UserAvatar } from "@/domain/user/components/user-avatar";
import { UserSelectDropdown } from "@/domain/user/components/user-select-dropdown";
import { User as UserModel } from "@/domain/user/models/user-model";
import { useWorkspaceMembershipList } from "@/domain/workspace/hooks/api/use-workspace-membership-list";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";

import { entityKey, type SelectedEntity, selectedEntityTypes, useCreateDataRequestStore } from "../create-data-request-state";
import { getAvailableFields } from "../data-request-fields";

export function ConfirmationStep() {
  const { t, i18n } = useDomainTranslation("data-request");
  const { currentWorkspace } = useCurrentWorkspaceContext();

  const title = useCreateDataRequestStore((s) => s.title);
  const dueDate = useCreateDataRequestStore((s) => s.dueDate);
  const selectedEntities = useCreateDataRequestStore((s) => s.selectedEntities);
  const fieldSelection = useCreateDataRequestStore((s) => s.fieldSelection);
  const enableDocuments = useCreateDataRequestStore((s) => s.enableDocuments);
  const freeTextQuestions = useCreateDataRequestStore((s) => s.freeTextQuestions);
  const confirmationAssigneeMap = useCreateDataRequestStore((s) => s.confirmationAssigneeMap);
  const setConfirmationAssignee = useCreateDataRequestStore((s) => s.setConfirmationAssignee);
  const clearConfirmationAssignee = useCreateDataRequestStore((s) => s.clearConfirmationAssignee);

  const { activeWorkspaceMembershipList } = useWorkspaceMembershipList({
    workspaceId: currentWorkspace.id,
  });

  const selectedEntityTypeList = useMemo(() => selectedEntityTypes(selectedEntities), [selectedEntities]);

  const availableFieldList = getAvailableFields(selectedEntityTypeList);
  const fieldCount = fieldSelection ? fieldSelection.length : availableFieldList.length;
  const freeTextQuestionCount = toTrimmedQuestionLabels(freeTextQuestions).length;

  const entitiesWithoutAssignee = useMemo(
    () => selectedEntities.filter((e) => !e.assigneeId),
    [selectedEntities],
  );

  const activeUserList: UserModel[] = useMemo(() => {
    return activeWorkspaceMembershipList
      .filter((m) => m.user)
      .map((m) => m.user!);
  }, [activeWorkspaceMembershipList]);

  const getDropdownUserList = (entity: SelectedEntity): UserModel[] => {
    if (entity.assigneeList.length > 1) {
      const entityAssigneeIdSet = new Set(entity.assigneeList.map((a) => a.userId));
      return activeUserList.filter((u) => entityAssigneeIdSet.has(u.id));
    }
    return activeUserList;
  };

  const handleAssigneeSelect = (entity: SelectedEntity, user: { id: string } | null) => {
    const key = entityKey(entity);
    if (!user) {
      clearConfirmationAssignee(key);
      return;
    }
    const fullUser = activeUserList.find((u) => u.id === user.id);
    setConfirmationAssignee(key, {
      assigneeId: user.id,
      assigneeName: fullUser?.fullname ?? fullUser?.email ?? "",
    });
  };

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-4 px-6 py-6">
      <h3 className="text-sm font-medium">{t("create.summary")}</h3>

      <div className="flex flex-col gap-2 rounded-md border p-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("create.titleLabel")}</span>
          <span className="font-medium">{title}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("create.dueDateLabel")}</span>
          <span>{dueDate ? dueDate.toLocaleDateString(i18n.language) : "—"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("create.stepEntities")}</span>
          <span>{t("create.entityCount", { count: selectedEntities.length })}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t("create.stepFields")}</span>
          <span>{t("create.fieldCount", { count: fieldCount })}</span>
        </div>
        {enableDocuments && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("create.fields.documents")}</span>
            <span>{t("create.documentsRequested")}</span>
          </div>
        )}
        {freeTextQuestionCount > 0 && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t("create.freeTextQuestionsLabel")}</span>
            <span>{t("create.freeTextQuestionCount", { count: freeTextQuestionCount })}</span>
          </div>
        )}
      </div>

      {entitiesWithoutAssignee.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
            <h4 className="text-sm font-medium">{t("create.missingAssigneeTitle")}</h4>
          </div>
          <p className="text-xs text-muted-foreground">{t("create.missingAssigneeDescription")}</p>
          <div className="flex flex-col gap-1 rounded-md border p-2">
            {entitiesWithoutAssignee.map((entity) => {
              const key = entityKey(entity);
              const assignment = confirmationAssigneeMap[key];
              const assignedUser = assignment
                ? activeUserList.find((u) => u.id === assignment.assigneeId)
                : undefined;

              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-2 px-2 py-1.5"
                >
                  <span className="min-w-0 truncate text-sm">{entity.entityTitle}</span>
                  <UserSelectDropdown
                    activeUsers={getDropdownUserList(entity)}
                    selected={assignment ? { id: assignment.assigneeId } : null}
                    onSelect={(user) => handleAssigneeSelect(entity, user)}
                    asChild
                  >
                    <Button variant="outline" size="sm" className="h-7 shrink-0 gap-1 text-xs">
                      {assignedUser ? (
                        <>
                          <UserAvatar user={assignedUser} size="xs" />
                          <span>{assignment!.assigneeName}</span>
                        </>
                      ) : (
                        <>
                          <User className="h-3 w-3" />
                          {t("create.selectAssignee")}
                        </>
                      )}
                    </Button>
                  </UserSelectDropdown>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
